import React, { useEffect, useRef, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Button, TextField, CircularProgress, Paper, Typography } from "@material-ui/core";
import { toast } from "react-toastify";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
  intro: { color: theme.palette.text.secondary, marginBottom: theme.spacing(2), maxWidth: 640 },
  uploadRow: {
    display: "flex", gap: theme.spacing(2), flexWrap: "wrap", alignItems: "center",
    padding: theme.spacing(2), marginBottom: theme.spacing(3)
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
    gap: theme.spacing(2)
  },
  card: { padding: theme.spacing(1.5), display: "flex", flexDirection: "column", gap: 6 },
  thumb: { width: "100%", height: 150, objectFit: "cover", borderRadius: 8, background: "#f1f5f9" },
  cap: { fontSize: "0.85rem", fontWeight: 600 },
  meta: { fontSize: "0.72rem", color: theme.palette.text.secondary, wordBreak: "break-all" },
  pathBox: {
    fontFamily: "monospace", fontSize: "0.68rem", background: "#f1f5f9",
    padding: "2px 6px", borderRadius: 4, cursor: "pointer", wordBreak: "break-all"
  }
}));

const AssistantUploads = () => {
  const classes = useStyles();
  const fileRef = useRef(null);
  const [caption, setCaption] = useState("");
  const [uploading, setUploading] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/assistant-uploads");
      setItems(Array.isArray(data?.items) ? data.items : []);
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const handleFile = async event => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("caption", caption);
      await api.post("/assistant-uploads", fd, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("Imagem enviada para o assistente");
      setCaption("");
      await load();
    } catch (err) {
      toastError(err);
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const copyPath = value => {
    try {
      navigator.clipboard.writeText(value);
      toast.success("Caminho copiado");
    } catch (err) {}
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>Imagens para o assistente</Title>
      </MainHeader>
      <Typography className={classes.intro}>
        Suba aqui qualquer foto ou print que voce queira que o assistente possa
        consultar. As imagens ficam guardadas no servidor e ele sempre tem acesso.
        Uma legenda ajuda a explicar o que e cada imagem.
      </Typography>

      <Paper variant="outlined" className={classes.uploadRow}>
        <TextField
          label="Legenda (o que e essa imagem)"
          value={caption}
          onChange={e => setCaption(e.target.value)}
          variant="outlined"
          size="small"
          style={{ flex: 1, minWidth: 240 }}
        />
        <Button variant="contained" color="primary" component="label" disabled={uploading}>
          {uploading ? <CircularProgress size={18} color="inherit" /> : "Enviar imagem"}
          <input ref={fileRef} type="file" accept="image/*" hidden onChange={handleFile} />
        </Button>
      </Paper>

      {loading ? (
        <CircularProgress />
      ) : items.length === 0 ? (
        <Typography color="textSecondary">Nenhuma imagem enviada ainda.</Typography>
      ) : (
        <div className={classes.grid}>
          {items.map(it => (
            <Paper key={it.filename} variant="outlined" className={classes.card}>
              <a href={it.url} target="_blank" rel="noreferrer">
                <img src={it.url} alt={it.caption || it.originalName} className={classes.thumb} />
              </a>
              {it.caption ? <span className={classes.cap}>{it.caption}</span> : null}
              <span className={classes.meta}>
                {it.originalName} · {new Date(it.at).toLocaleString("pt-BR")}
              </span>
              <span className={classes.pathBox} title="Clique para copiar o caminho no servidor" onClick={() => copyPath(it.path)}>
                {it.path}
              </span>
            </Paper>
          ))}
        </div>
      )}
    </MainContainer>
  );
};

export default AssistantUploads;
